/**
 * ModuleGate
 *
 * Route-level guard that enforces per-module access control.
 * - 'none'  → redirects to /
 * - 'read'  → renders children inside a ModuleAccessContext('read')
 * - 'edit'  → renders children inside a ModuleAccessContext('edit')
 *
 * Wrap each gated route in App.tsx:
 *   <Route path="/assets" element={<ModuleGate module="assets"><Assets /></ModuleGate>} />
 */

import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@store/authStore';
import { resolveModuleAccess } from '@utils/permissions';
import { ModuleAccessContext } from '@utils/moduleAccess';
import type { ModuleId } from '@utils/moduleAccess';

interface ModuleGateProps {
  module: ModuleId;
  children: React.ReactNode;
}

export function ModuleGate({ module, children }: ModuleGateProps) {
  const { teamRole, modulePermissions } = useAuthStore();
  const access = resolveModuleAccess(module, teamRole, modulePermissions);

  if (access === 'none') {
    return <Navigate to="/" replace />;
  }

  return (
    <ModuleAccessContext.Provider value={access}>
      {children}
    </ModuleAccessContext.Provider>
  );
}
